/**
 * Language Context
 * Uses supportedLanguages from i18n config as single source of truth.
 */

import React, { createContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supportedLanguages } from '../i18n/index';

export const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  
  // Normalize detected language (e.g. 'en-US' → 'en')
  const normalizeCode = (code) => {
    const short = (code || 'en').split('-')[0].toLowerCase();
    // Ensure it's a supported language, fallback to 'en'
    return supportedLanguages.some(l => l.code === short) ? short : 'en';
  };
  
  const [language, setLanguage] = useState(normalizeCode(i18n.language));

  const changeLanguage = async (lng) => {
    await i18n.changeLanguage(lng);
    setLanguage(lng);
    document.documentElement.setAttribute('lang', lng);
    localStorage.setItem('i18nextLng', lng);
  };

  // Sync if i18n language changes externally
  useEffect(() => {
    const handleLangChanged = (lng) => {
      const normalized = normalizeCode(lng);
      setLanguage(normalized);
      document.documentElement.setAttribute('lang', normalized);
    };
    i18n.on('languageChanged', handleLangChanged);
    return () => i18n.off('languageChanged', handleLangChanged);
  }, [i18n]);

  useEffect(() => {
    document.documentElement.setAttribute('lang', language);
  }, [language]);

  const currentLanguageInfo = supportedLanguages.find(l => l.code === language) || supportedLanguages[0];

  return (
    <LanguageContext.Provider value={{ language, currentLanguage: language, changeLanguage, supportedLanguages, currentLanguageInfo }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
